import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { useUserStore } from '@/config/store/userStore'

const resource = 'salesAi/v2'

type AskAttachment = {
  name: string
  mimeType: string
  dataBase64: string
}

type AskMessage = {
  role: 'user' | 'assistant'
  content: string
  attachments?: AskAttachment[]
}

type AskPayload = {
  question: string
  language?: string
  messages?: AskMessage[]
  attachments?: AskAttachment[]
}

type AskCitation = {
  type: string
  _id: string
  display: string
  companyId?: string | null
}

type AskUsage = {
  promptTokens?: number
  inputTokens?: number
  cachedTokens?: number
  completionTokens?: number
  totalTokens?: number
}

export type AskStreamHandlers = {
  onToken?: (text: string) => void
  onDone?: (payload: {
    answer: string
    citations: AskCitation[]
    usage?: AskUsage
  }) => void
  onError?: (message: string) => void
  signal?: AbortSignal
}

const ask =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (data: AskPayload) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/ask`,
      data,
    })
  }

const askStream = () =>
  async (data: AskPayload, handlers: AskStreamHandlers = {}) => {
    const userStore = useUserStore()
    const token = userStore?.token
    if (!token) {
      throw new Error('Missing auth token')
    }

    const response = await fetch(`${BASE_URL}/${API_MAIN_VERSION}/${resource}/ask/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
      signal: handlers.signal,
    })

    if (!response.ok) {
      let message = `Ask CRM stream failed (${response.status})`
      try {
        const errBody = await response.json()
        if (errBody?.customMessage) message = errBody.customMessage
      }
      catch {
        // ignore parse errors
      }
      throw new Error(message)
    }

    if (!response.body) {
      throw new Error('Ask CRM stream empty body')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let receivedDone = false

    const handleEvent = (event: any) => {
      if (!event || typeof event !== 'object') return
      if (event.type === 'token' && typeof event.text === 'string') {
        handlers.onToken?.(event.text)
        return
      }
      if (event.type === 'done') {
        receivedDone = true
        handlers.onDone?.({
          answer: event.answer || '',
          citations: Array.isArray(event.citations) ? event.citations : [],
          usage: event.usage,
        })
        return
      }
      if (event.type === 'error') {
        handlers.onError?.(event.message || 'Ask CRM stream failed')
      }
    }

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const chunks = buffer.split('\n\n')
      buffer = chunks.pop() || ''
      for (const chunk of chunks) {
        const dataLine = chunk
          .split('\n')
          .map(line => line.trimEnd())
          .find(line => line.startsWith('data:'))
        if (!dataLine) continue
        const raw = dataLine.replace(/^data:\s?/, '')
        if (!raw || raw === '[DONE]') continue
        try {
          handleEvent(JSON.parse(raw))
        }
        catch {
          // ignore malformed SSE frames
        }
      }
    }

    if (buffer.trim()) {
      const dataLine = buffer
        .split('\n')
        .map(line => line.trimEnd())
        .find(line => line.startsWith('data:'))
      if (dataLine) {
        const raw = dataLine.replace(/^data:\s?/, '')
        if (raw && raw !== '[DONE]') {
          try {
            handleEvent(JSON.parse(raw))
          }
          catch {
            // ignore
          }
        }
      }
    }

    if (!receivedDone) {
      throw new Error('Ask CRM stream ended without done event')
    }
  }

const research =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (data: { companyId: string; engagementId?: string | null; language?: string }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/research`,
      data,
    })
  }

const followUp =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (data: { companyId?: string | null; engagementId?: string | null; language?: string }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/follow-up`,
      data,
    })
  }

const listMcpTools = (axios: IFactoryFunctionRequest<any, any>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/mcp/tools`,
  })
}

const callMcpTool =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (name: string, args: Record<string, unknown> = {}) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/mcp/call`,
      data: { name, arguments: args },
    })
  }

export default (axios: AxiosInstance) => ({
  ask: ask(useAxios(axios)),
  askStream: askStream(),
  research: research(useAxios(axios)),
  followUp: followUp(useAxios(axios)),
  listMcpTools: listMcpTools(useAxios(axios)),
  callMcpTool: callMcpTool(useAxios(axios)),
})
